import { ITableHeader } from "@/types/table";
import { useNotification } from "@/utils/notification";
import { defineStore } from "pinia";
import {
  IFilterEligibleTasks,
  IFilterQaReviews,
  IManualSelectionPayload,
  IRandomSelectionPayload,
} from "@/features/check/check.types";
import { ref, Ref } from "vue";
import { checkService } from "@/features/check/check.service";
import { useUserStore } from "@/features/users/user.store";
import router from "@/router";

const { pushNotification } = useNotification();

export const useCheckStore = defineStore("check", () => {
  const userStore = useUserStore();

  const checkHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      width: "3%",
    },
    {
      name: "company",
      label: "Company",
      width: "14%",
    },
    {
      name: "drivers",
      label: "Drivers",
      width: "10%",
    },
    {
      name: "users",
      label: "User",
      width: "10%",
    },
    {
      name: "date",
      label: "Check time",
      width: "7%",
    },
    {
      name: "check-date",
      label: "Task time",
      width: "7%",
    },
    {
      name: "status",
      label: "Status",
      width: "6%",
    },
    {
      name: "problems",
      label: "Problems",
      width: "14%",
    },
    {
      name: "checked",
      label: "checked",
      width: "10%",
    },
    {
      name: "check",
      label: "checkStatus",
      width: "9%",
    },
    {
      name: "actions",
      label: "",
      width: "5%",
    },
  ];

  const commonFilterQaReview = ref<IFilterQaReviews>({
    page: 1,
    per_page: 50,
    search: "",
    team_id: "",
    assigned_to_id: "",
    review_status: undefined,
    selection_type: undefined,
    work_type_id: "",
    organization_id: "",
    result: undefined,
    shift_id: "",
    worker_id: "",
  });

  const clearFilter = () => {
    commonFilterQaReview.value = {
      page: 1,
      per_page: 50,
      search: "",
      team_id: "",
      assigned_to_id: "",
      review_status: undefined,
      selection_type: undefined,
      work_type_id: "",
      organization_id: "",
      result: undefined,
      shift_id: "",
      worker_id: "",
    };
  };

  async function getEligibleTasks(
    loading: Ref<boolean>,
    filter: IFilterEligibleTasks,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .getEligibleTasks(filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getManualQaReviews(
    loading: Ref<boolean>,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    commonFilterQaReview.value.organization_id = localStorage.getItem(
      "organizationId"
    )
      ? (localStorage.getItem("organizationId") as string)
      : router.push("/organization");

    await checkService
      .getManualQaReviews({ ...commonFilterQaReview.value })
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function createManualSelection(
    loading: Ref<boolean>,
    payload: IManualSelectionPayload,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .createManualSelection(payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function createRandomSelection(
    loading: Ref<boolean>,
    payload: IRandomSelectionPayload,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .createRandomSelection(payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function startManualQaReview(
    loading: Ref<boolean>,
    review_id: string,
    filter: { organization_id: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .startManualQaReview(review_id, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function holdManualQaReview(
    loading: Ref<boolean>,
    review_id: string,
    hold_reason: string,
    filter: { organization_id: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .holdManualQaReview(review_id, hold_reason, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function resumeManualQaReview(
    loading: Ref<boolean>,
    review_id: string,
    filter: { organization_id: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .resumeManualQaReview(review_id, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function completeManualQaReview(
    loading: Ref<boolean>,
    review_id: string,
    result: string,
    comment: string,
    filter: { organization_id: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .completeManualQaReview(review_id, result, comment, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function archiveQaReview(
    loading: Ref<boolean>,
    review_id: string,
    filter: { organization_id: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await checkService
      .archiveQaReview(review_id, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    checkHeaders,
    commonFilterQaReview,
    clearFilter,
    getEligibleTasks,
    getManualQaReviews,
    createManualSelection,
    createRandomSelection,
    startManualQaReview,
    holdManualQaReview,
    resumeManualQaReview,
    completeManualQaReview,
    archiveQaReview,
  };
});
